// src/components/student/StudentStats.js

import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { getDatabase, get, ref } from 'firebase/database';
import {
  Activity,
  CheckCircle2,
  Clock3,
  Flame,
  Layers3,
  MousePointerClick,
  Sparkles,
  Target,
} from 'lucide-react';
import Preloader from '../Preloader';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const pct = (value, digits = 1) => Number.isFinite(value) ? Number(value).toFixed(digits) : '0.0';
const sum = (values) => values.reduce((total, value) => total + Number(value || 0), 0);
const questionCount = (session) => session.total_questions || Object.keys(session.questions || {}).length;
const sessionLabel = (session, index) => {
  const date = session.start_date_time ? new Date(session.start_date_time) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : `S${index + 1}`;
};

const emptyStats = {
  grade: 0,
  points: 0,
  possible: 0,
  attended: 0,
  totalSessions: 0,
  attendanceRate: 0,
  answered: 0,
  totalQuestions: 0,
  responseRate: 0,
  answerChanges: 0,
  bestSession: null,
  focusSession: null,
  streak: 0,
  typeRows: [],
  sessionRows: [],
  attendanceData: {},
};

const InsightCard = ({ icon: Icon, label, value }) => (
  <div className="ce-stat-3d">
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--muted-color)' }}>
        {label}
      </span>
      <span className="rounded-lg p-2" style={{ color: 'var(--primary-color)', background: 'var(--alt-bg)' }}>
        <Icon size={17} aria-hidden="true" />
      </span>
    </div>
    <div className="mt-3 text-3xl font-bold" style={{ color: 'var(--text-color)' }}>{value}</div>
  </div>
);

const StudentStats = ({ grades, sessions, classId, user }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(emptyStats);
  const isDark = document.documentElement.classList.contains('dark');
  const chartTextColor = isDark ? '#f7f7f8' : '#0a0a0a';
  const chartGridColor = isDark ? '#2a2a2e' : '#ececee';

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: chartTextColor } },
    },
    scales: {
      x: { ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
      y: { beginAtZero: true, ticks: { color: chartTextColor }, grid: { color: chartGridColor } },
    },
  }), [chartGridColor, chartTextColor]);

  const pieOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: chartTextColor } },
    },
  }), [chartTextColor]);

  useEffect(() => {
    let cancelled = false;

    const calculateStats = async () => {
      setLoading(true);
      try {
        const dbRealtime = getDatabase();
        const totalQuestions = sessions.reduce((total, session) => total + questionCount(session), 0);
        const points = Object.values(grades || {}).reduce((sessionTotal, sessionData) => {
          return sessionTotal + sum(Object.values(sessionData.points || {}));
        }, 0);
        const grade = totalQuestions > 0 ? (points / totalQuestions) * 100 : 0;

        const [attendanceSnaps, answerSnaps] = await Promise.all([
          Promise.all(sessions.map((session) => get(ref(dbRealtime, `attendance/${classId}/${session.id}/userIds`)))),
          Promise.all(sessions.map((session) => get(ref(dbRealtime, `answers/${classId}/${session.id}`)))),
        ]);

        const attendanceBySession = {};
        attendanceSnaps.forEach((snap, index) => {
          attendanceBySession[sessions[index].id] = snap.exists() ? snap.val() || [] : [];
        });

        const answersBySession = {};
        answerSnaps.forEach((snap, index) => {
          answersBySession[sessions[index].id] = snap.exists() ? snap.val() || {} : {};
        });

        let answered = 0;
        let answerChanges = 0;
        const typeStats = {};

        const sessionRows = sessions.map((session, index) => {
          const sessionGrades = grades?.[session.id] || {};
          const possible = questionCount(session);
          const earned = sum(Object.values(sessionGrades.points || {}));
          const percent = possible ? (earned / possible) * 100 : 0;
          const attended = (attendanceBySession[session.id] || []).includes(user.uid) || Boolean(grades?.[session.id]);
          let sessionAnswered = 0;

          Object.entries(session.questions || {}).forEach(([questionNumber, question]) => {
            const entry = answersBySession[session.id]?.[questionNumber]?.[user.uid];
            const type = question.question_type || 'unknown';
            typeStats[type] = typeStats[type] || { type, points: 0, possible: 0, answered: 0 };
            typeStats[type].possible += 1;

            const earnedPoint = Number(sessionGrades.points?.[questionNumber] || 0);
            typeStats[type].points += earnedPoint;

            if (entry && entry.answer !== null && entry.answer !== undefined) {
              answered += 1;
              sessionAnswered += 1;
              typeStats[type].answered += 1;
              const historyCount = entry.history ? Object.keys(entry.history).length : 0;
              answerChanges += Math.max(0, historyCount - 1);
            }
          });

          return {
            label: sessionLabel(session, index),
            percent,
            points: earned,
            possible,
            attended,
            answered: sessionAnswered,
          };
        });

        let streak = 0;
        for (let i = sessionRows.length - 1; i >= 0; i -= 1) {
          if (!sessionRows[i].attended) break;
          streak += 1;
        }

        const attended = sessionRows.filter((row) => row.attended).length;
        const gradedSessions = sessionRows.filter((row) => row.possible > 0);
        const bestSession = [...gradedSessions].sort((a, b) => b.percent - a.percent)[0] || null;
        const focusSession = [...gradedSessions].sort((a, b) => a.percent - b.percent)[0] || null;

        const typeRows = Object.values(typeStats).map((row) => ({
          label: row.type.replaceAll('_', ' '),
          grade: row.possible ? (row.points / row.possible) * 100 : 0,
          response: row.possible ? (row.answered / row.possible) * 100 : 0,
        }));

        if (!cancelled) {
          setStats({
            grade,
            points,
            possible: totalQuestions,
            attended,
            totalSessions: sessions.length,
            attendanceRate: sessions.length ? (attended / sessions.length) * 100 : 0,
            answered,
            totalQuestions,
            responseRate: totalQuestions ? (answered / totalQuestions) * 100 : 0,
            answerChanges,
            bestSession,
            focusSession,
            streak,
            typeRows,
            sessionRows,
            attendanceData: {
              labels: ['Attended', 'Missed'],
              datasets: [
                {
                  data: [attended, Math.max(sessions.length - attended, 0)],
                  backgroundColor: ['#1f8a4c', '#d65656'],
                },
              ],
            },
          });
        }
      } catch (error) {
        console.error('Error calculating stats:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    calculateStats();
    return () => {
      cancelled = true;
    };
  }, [classId, grades, sessions, user]);

  const progressData = {
    labels: stats.sessionRows.map((row) => row.label),
    datasets: [
      {
        label: 'Session score',
        data: stats.sessionRows.map((row) => row.percent),
        borderColor: '#c43a3a',
        backgroundColor: 'rgba(196, 58, 58, 0.14)',
        tension: 0.35,
        fill: true,
      },
    ],
  };

  const typeData = {
    labels: stats.typeRows.map((row) => row.label),
    datasets: [
      {
        label: 'Grade',
        data: stats.typeRows.map((row) => row.grade),
        backgroundColor: '#c43a3a',
        borderRadius: 8,
      },
      {
        label: 'Response',
        data: stats.typeRows.map((row) => row.response),
        backgroundColor: '#1f5fbf',
        borderRadius: 8,
      },
    ],
  };

  if (loading) return <Preloader />;

  return (
    <div className="ce-analytics-page">
      <div className="ce-analytics-hero">
        <div>
          <p className="ce-eyebrow">Personal pulse</p>
          <h3>Your individual analytics</h3>
        </div>
        <div className="ce-performance-orb" style={{ '--score': `${Math.min(stats.grade, 100) * 3.6}deg` }}>
          <span>{pct(stats.grade, 0)}%</span>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <InsightCard icon={Target} label="Grade" value={`${pct(stats.grade)}%`} />
        <InsightCard icon={CheckCircle2} label="Points" value={`${pct(stats.points, 1)}/${stats.possible}`} />
        <InsightCard icon={Activity} label="Attendance" value={`${pct(stats.attendanceRate)}%`} />
        <InsightCard icon={MousePointerClick} label="Response" value={`${pct(stats.responseRate)}%`} />
        <InsightCard icon={Flame} label="Streak" value={stats.streak} />
        <InsightCard icon={Clock3} label="Answer changes" value={stats.answerChanges} />
        <InsightCard icon={Sparkles} label="Best session" value={stats.bestSession ? `${pct(stats.bestSession.percent, 0)}%` : '0%'} />
        <InsightCard icon={Layers3} label="Focus session" value={stats.focusSession ? `${pct(stats.focusSession.percent, 0)}%` : '0%'} />
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[1.45fr_1fr]">
        <div className="ce-analytics-card">
          <h4>Progress curve</h4>
          <div className="h-[320px]">
            <Line data={progressData} options={chartOptions} />
          </div>
        </div>
        <div className="ce-analytics-card">
          <h4>Attendance split</h4>
          <div className="h-[320px]">
            <Pie data={stats.attendanceData} options={pieOptions} />
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[1fr_0.9fr]">
        <div className="ce-analytics-card">
          <h4>Question type profile</h4>
          <div className="h-[300px]">
            <Bar data={typeData} options={chartOptions} />
          </div>
        </div>
        <div className="ce-analytics-card">
          <h4>Session detail</h4>
          <div className="space-y-3">
            {stats.sessionRows.map((row) => (
              <div key={row.label} className="ce-progress-row">
                <div className="flex items-center justify-between gap-3">
                  <span>{row.label}</span>
                  <span>{pct(row.percent)}%</span>
                </div>
                <div className="ce-progress-track">
                  <div style={{ width: `${Math.min(row.percent, 100)}%` }} />
                </div>
              </div>
            ))}
            {stats.sessionRows.length === 0 && <div className="ce-empty">No session data yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentStats;
